<template>
    <div class="mt-5">
        <attorney-service-filter-modal :status-options="statuses" :tag-options="tags"></attorney-service-filter-modal>
        <div :class="[{'border-bottom': collapsed}, 'd-flex justify-content-between align-items-end']">
            <div class="h4">
                <a href="#servicesTable" class="mr-2" role="button" @click="collapsed = !collapsed">
                    <i :class="['fas text-muted', collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                </a>
                Health Insurance Claim Forms
                <span class="font-xs font-italic text-muted">
                    (CMS 1500/1450)
                </span>                          
            </div>
            <div v-if="!collapsed" class="btn-toolbar" role="toolbar">
                <div class="input-group input-group-sm mb-1">
                    <div class="input-group-prepend">
                        <span class="input-group-text fas fa-search fa-sm text-muted"></span>
                        <button class="btn dropdown-toggle input-group-text" type="button" data-toggle="dropdown">{{ searchOption.name }}</button>
                        <div class="dropdown-menu">
                            <button v-for="option in searchOptions" :key="'search-option-' + option.id" class="dropdown-item text-primary font-sm" @click="searchOption = option">{{ option.name }}</button>
                        </div>                        
                    </div>
                    <input :type="searchOption.type" class="form-control" :size="searchInputSize" :placeholder="searchOption.placeholder" v-model="searchString">
                </div>
                <div class="btn-group ml-2"> 
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Filter" @click="showFilters"><i class="fas fa-filter fa-lg"></i></button>
                </div>
            </div>            
        </div>
        <div id="servicesTable" :class="['collapse', { 'show': !collapsed }]">
            <div v-if="loading" class="pt-3">            
                <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
            </div>
            <div v-else-if="documents.length === 0" class="border-top"><h3 class="text-muted text-center mt-3">No Documents.</h3></div>
            <div v-else>            
                <table class="table table-sm">
                    <thead>
                        <tr>
                            <th></th>
                            <th class="fit">Document Type</th>
                            <th>PAN</th>
                            <th>DOS</th>
                            <th>Description</th>
                            <th>Owner</th>
                            <th>Uploaded</th>
                            <th>Submitted</th>
                            <th>Estimated Reimbursement</th>
                            <th>Balance</th>
                            <th>Tags</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="doc in filteredDocuments">
                            <tr class="table-stripe" :key="getDocumentKey(doc)">
                                <td class="fit">
                                    <div class="d-flex justify-content-start align-items-center">
                                        <div>
                                            <a v-if="doc.associated_documents && doc.associated_documents.length" @click="doc.collapsed = !doc.collapsed">
                                                <i :class="['fas fa-lg fa-fw text-muted', doc.collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                                            </a>
                                            <i v-else class="fas fa-lg fa-fw"></i>
                                        </div>
                                        <div><i :class="['fas fa-sm fa-fw', {'fa-link text-muted': doc.is_linked}]"></i></div>
                                        <div>                                        
                                            <document-status-icon v-if="doc.document_status" :document-id="doc.id" :document-status="doc.document_status"></document-status-icon>
                                            <i v-else :class="['fas fa-lg fa-fw', {'fa-history text-muted': doc.historical}]"></i>
                                        </div>
                                    </div>                                    
                                </td>
                                <td class="fit">
                                    <document-type-icon :document="doc" :href="getDocumentUrl(doc)"></document-type-icon>                                  
                                </td>
                                <td class="fit"><a :href="getServiceUrl(doc)">{{ doc.pan }}</a></td>
                                <td class="fit">{{ doc.dos }}</td>
                                <td class="text-truncate">{{ doc.description }}</td>
                                <td class="fit">{{ doc.owner }}</td>
                                <td class="fit">{{ doc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                <td class="fit">{{ getSubmitDate(doc) }}</td>
                                <td class="fit text-right">{{ doc.estimated_reimbursement | currency }}<sup v-if="doc.contract_applied">*</sup></td>
                                <td :class="['fit text-right pr-3', { 'text-danger' : doc.balance > 0}]">{{ doc.balance | currency }}</td>
                                <td class="fit pr-1">
                                    <document-tags-button :document="doc"></document-tags-button>
                                </td>
                                <td class="fit p-0">
                                    <div class="d-flex flex-nowrap flex-row justify-content-end">

                                        <!-- fill empty space -->
                                        <div class="flex-fill"></div>

                                        <!-- submitted by mail -->
                                        <div class="mx-1">
                                            <mailed-document-icon v-if="doc.mailed_date || doc.received_date" :mailed-date="doc.mailed_date" :received-date="doc.received_date"></mailed-document-icon>
                                            <i v-else class="fas fa-lg fa-fw"></i>
                                        </div>  

                                        <!-- show submission history -->
                                        <div class="mx-1">
                                            <button v-if="doc.show_submission_history" class="btn btn-link p-0" title="View Submission History" @click="showSubmissionHistory(doc)">
                                                <i class="fas fa-history fa-lg"></i>
                                            </button>
                                            <i v-else class="fas fa-lg fa-fw"></i>
                                        </div>                                                                             

                                        <!-- document notes -->
                                        <div class="mx-1">
                                            <button class="btn btn-link p-0" title="Add/View Document Notes" @click="onDocumentNotes(doc)">
                                                <i class="fas fa-comment fa-lg fa-fw"></i>
                                            </button>
                                        </div>

                                        <!-- associate document with a 1008 -->
                                        <div class="mx-1">
                                            <button v-if="disputeCount && doc.can_associate" class="btn btn-link btn-sm p-0" title="Link Document" @click="associate(doc)">
                                                <i class="fas fa-link fa-lg fa-fw"></i>
                                            </button>
                                            <i v-else class="fas fa-lg fa-fw"></i>                                      
                                        </div>                                        

                                    </div>                           
                                </td>
                            </tr>

                            <template v-if="doc.confirmations && doc.confirmations.length">
                                <tr v-show="!doc.collapsed" v-for="confirmation in doc.confirmations" :key="getConfirmationKey(doc.document_id, confirmation)">
                                    <td class="fit px-0">
                                        <div class="d-flex justify-content-start align-items-center">
                                            <div><i class="fas fa-lg fa-fw"></i></div>
                                            <div><i :class="['fas fa-sm fa-fw', {'fa-link text-muted': confirmation.is_linked}]"></i></div>
                                            <div><i class="fas fa-lg fa-fw"></i></div>
                                        </div>
                                    </td>
                                    <td colspan="3" class="fit">
                                        <div class="d-flex justify-content-start align-items-center">
                                            <div><i class="fas fa-lg fa-fw align-middle"></i></div>
                                            <div><i class="fas fa-sm fa-fw"></i></div>
                                            <div>
                                                <i class="fas fa-clipboard-check fa-lg fa-fw text-muted mr-1"></i>
                                                <a :href="confirmation.href" target="_blank">{{ confirmation.transmission_method_name }} Confirmation</a>
                                            </div>
                                        </div>
                                    </td>
                                    <td>Confirmation received at {{ confirmation.completed_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>                                    
                                    <td></td>
                                    <td></td>
                                    <td></td>                                     
                                    <td class="fit p-0">
                                        <div class="d-flex flex-nowrap flex-row justify-content-end">

                                            <!-- fill empty space -->
                                            <div class="flex-fill"></div>

                                            <div class="mx-1">
                                                <button v-if="disputeCount && confirmation.can_associate" class="btn btn-link btn-sm p-0" title="Link Document" @click="associate(confirmation, true)" :disabled="!doc.is_linked">
                                                    <i class="fas fa-link fa-lg fa-fw"></i>
                                                </button>
                                                <i v-else class="fas fa-lg fa-fw"></i>                                      
                                            </div>                                            
                                        </div>
                                    </td>                                    
                                </tr>
                            </template>                            

                            <template v-if="doc.associated_documents && doc.associated_documents.length">
                                <tr v-show="!doc.collapsed" v-for="(associatedDoc, index) in doc.associated_documents" :key="getAssociatedDocumentKey(doc, associatedDoc.document_id, index)">
                                    <td class="fit px-0">
                                        <div class="d-flex justify-content-start align-items-center">
                                            <div><i class="fas fa-lg fa-fw"></i></div>
                                            <div><i :class="['fas fa-sm fa-fw', {'fa-link text-muted': associatedDoc.is_linked}]"></i></div>
                                            <div>
                                                <document-status-icon v-if="associatedDoc.document_status" :document-id="associatedDoc.document_id" :document-status="associatedDoc.document_status"></document-status-icon>
                                                <i v-else class="fas fa-lg fa-fw"></i>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="fit">
                                        <div class="d-flex justify-content-start align-items-center">
                                            <!-- using an empty icon element to get consistent indentation -->
                                            <div><i class="fas fa-lg fa-fw align-middle"></i></div>
                                            <div><i :class="['fas fa-sm fa-fw align-middle', {'fa-history text-muted mb-1': associatedDoc.historical}]"></i></div>
                                            <div><document-type-icon :document="associatedDoc" :href="getDocumentUrl(associatedDoc)"></document-type-icon></div>
                                        </div>                           
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td>{{ associatedDoc.description | truncate(80) }}</td>
                                    <td class="fit">{{ associatedDoc.owner }}</td>
                                    <td class="fit">{{ associatedDoc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                    <td class="fit">{{ associatedDoc.submitted_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td class="fit p-0">
                                        <div class="d-flex flex-nowrap flex-row justify-content-end">

                                            <!-- fill empty space -->
                                            <div class="flex-fill"></div>

                                            <div class="mx-1">
                                                <button v-if="disputeCount && associatedDoc.can_associate" class="btn btn-link btn-sm p-0" title="Link Document" @click="associate(associatedDoc)">
                                                    <i class="fas fa-link fa-lg fa-fw"></i>
                                                </button>
                                                <i v-else class="fas fa-lg fa-fw"></i>                                      
                                            </div>                                            
                                        </div>
                                    </td>
                                </tr>
                            </template>                            

                        </template>
                    </tbody>
                    <caption v-if="contractFootnote" class="font-italic text-left p-0">
                        * <small>{{ ContractDisclaimer }}</small>
                    </caption>
                </table>
            </div>            
        </div>
    </div>    
</template>

<script>
import { DocumentType, HistoricalState, ContractDisclaimer, TransmissionMethod } from '../../constants.js';
import DocumentStatusIcon from '../documents/DocumentStatusIconComponent.vue';
import DocumentTypeIcon from '../documents/DocumentTypeIconComponent.vue';
import MailedDocumentIcon from '../icons/MailedDocumentIconComponent.vue';
import DocumentTagsButton from '../buttons/DocumentTagsButtonComponent.vue';
import AttorneyServiceFilterModal from './AttorneyFilterDocumentsComponent.vue';

export default {
    components: {
        DocumentStatusIcon,
        DocumentTypeIcon,
        MailedDocumentIcon,
        DocumentTagsButton,
        AttorneyServiceFilterModal
    },
    props: {
        clientId: Number,
        claimId: Number,
        href: String,
        state: Number
    },
    data() {
        return {

            // constants
            DocumentType: DocumentType,
            HistoricalState: HistoricalState,
            ContractDisclaimer: ContractDisclaimer,
            TransmissionMethod: TransmissionMethod,
            DESCRIPTION_OPTION: 1,
            PAN_OPTION: 2,
            DOS_OPTION: 3,

            loading: false,
            collapsed: false,
            searchOptions: [],
            searchOption: null,
            searchInputSize: 30,
            searchString: '',
            filters: {
                link_status: 'all',
                status: '',
                tagInclusion: 'include',
                tag: '',
                dos: {
                    begin: null,
                    end: null
                },
                submitted: {
                    begin: null,
                    end: null
                },
                balance: {
                    operator: 'any',
                    min: null,
                    max: null
                }                
            },            
            documents: [],
            disputeCount: 0
        }
    },
    computed: {
        filteredDocuments: function () {

            let self = this;
            let retval = self.documents;
  
            // filter on historical state
            if (this.state !== HistoricalState.ALL) {
                retval = retval.filter(x => x.historical === (self.state === HistoricalState.ONLY));
            }

            if (self.filters) {     
                                
                // filter on tag
                if (self.filters.tagInclusion === 'include') {
                    
                    // if a tag has been set then filter the documents on that tag
                    if (self.filters.tag.trim() !== '') { 
                        retval = retval.filter(e => (e.tags.some(t => t.tag.toLowerCase() === self.filters.tag.toLowerCase())));
                    }
                }
                else if (self.filters.tagInclusion === 'exclude') {

                    // if a tag has been set return all documents that don't have that tag
                    if (self.filters.tag.trim() !== '') {
                        retval = retval.filter(e => (!e.tags.some(t => t.tag.toLowerCase() === self.filters.tag.toLowerCase())));
                    }
                    else {
                        retval = retval.filter(e => !e.tags.length);
                    }
                }

                // filter on linked status
                if (self.filters.link_status && self.filters.link_status != 'all') {

                    retval = retval.filter(e => {
                        return (self.filters.link_status === 'linked') ? e.is_linked : !e.is_linked;
                    });
                }                

                // filter on status
                if (self.filters.status !== '') {
                    retval = retval.filter(e => (e.document_status && (e.document_status.id == self.filters.status)));
                }

                // filter on owner
                if (self.filters.owner) {
                    retval = retval.filter(e => (e.owner && (e.owner.toLowerCase() === self.filters.owner.toLowerCase())));
                }
                
                // filter on date of service
                if (self.filters.dos.begin && self.filters.dos.end) {
                    retval = retval.filter(e => {
                        return (e.dos >= self.filters.dos.begin && e.dos <= self.filters.dos.end);
                    });
                }                

                // filter on submitted_at date; we have to convert the date to local time and drop the time
                // portion in order to do the comparison properly
                if (self.filters.submitted.begin && self.filters.submitted.end) {
                    retval = retval.filter(e => {
                        let submittedAt = e.historical ? e.submit_date : self.getLocalDate(e.submitted_at);
                        return (submittedAt >= self.filters.submitted.begin && submittedAt <= self.filters.submitted.end);
                    });
                }

                // filter on balance
                if (self.filters.balance.operator && (self.filters.balance.operator !== 'any') && this.filters.balance.min) {

                    switch(self.filters.balance.operator)
                    {
                        case '=':
                            retval = retval.filter(e => parseFloat(e.balance) === parseFloat(this.filters.balance.min));
                            break;
                        case '!=':
                            retval = retval.filter(e => parseFloat(e.balance) != parseFloat(this.filters.balance.min));
                            break;                                
                        case '<':
                            retval = retval.filter(e => parseFloat(e.balance) < parseFloat(this.filters.balance.min));
                            break;                                
                        case '>':
                            retval = retval.filter(e => parseFloat(e.balance) > parseFloat(this.filters.balance.min));
                            break;                                
                        case 'between':
                            if (this.filters.balance.max) {
                                retval = retval.filter(e => (e.balance && (parseFloat(e.balance) >= parseFloat(self.filters.balance.min)) && 
                                    (parseFloat(e.balance) <= parseFloat(self.filters.balance.max))));
                            }
                            break; 
                    }
                }                
            }            

            // filter on description
            return retval.filter(function (doc) {
                return (doc[self.searchOption.property].toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1);
            });
        },
        statuses: function () {
            // make sure we filter out nulls (historical service forms won't have a status)
            let statuses = this.documents.filter(e => e.document_status).map(d => d.document_status);
            return statuses.filter((e, index, self) => {
                return self.findIndex(x =>  x.id === e.id) === index;
            });
        },
        tags: function () {
            return [...new Set(this.documents.map(d => d.tags.map(t => t.tag)).flat(1))]
        },
        contractFootnote: function () {
            return this.filteredDocuments.some(x => x.contract_applied);
        }                 
    },
    methods: {
        refresh: function (reset) {

            let self = this;
            this.loading = true;

            axios.get(`/ajax/attorney/claims/${self.claimId}/documents/services`)
                .then(response => {

                    // set the dispute count so we know if documents can be linked
                    this.disputeCount = response.data.dispute_count;

                    // reset the document section collapse state (user preferences)
                    if (reset) {
                        self.collapsed = response.data.collapsed;
                    }                    

                    // retain the previous document collapsed states
                    let collapseStates = self.documents.map(d => { return {id: d.id, collapsed: d.collapsed}; });   

                    // set the collapsed flag for each document 
                    self.documents = response.data.documents.map(e => {

                        // add edit flag to each document
                        self.$set(e, 'edit', {'description': false, 'submit_date': false});

                        if (reset) {
                            self.$set(e, 'collapsed', true);
                        }
                        else {
                            let doc = collapseStates.find(b => b.id === e.id);                            
                            self.$set(e, 'collapsed', doc ? doc.collapsed : false);                            
                        }

                        return e; 
                    });
                    
                    // enable popovers after the DOM has updated
                    self.$nextTick(() => {
                        $('[data-toggle="popover"]').popover();                      
                    });                    

                    self.loading = false;
                })
                .catch(error => {
                    console.log(error);
                    self.loading = false;
                });
        },
        showFilters: function () {
            eventHub.$emit('show-attorney-document-filters', this.filters, this.filter);
        },
        filter: function (filters) {
            this.filters = filters;
        },
        getDocumentKey: function (doc) {

            let token = doc.historical ? 'historical-' : '';
            return 'attorney-' + token + 'service-documents-' + doc.id;
        },
        getConfirmationKey: function (documentId, confirmation) {
            return `attorney-${documentId}-${confirmation.transmission_method_name}-${confirmation.outbound_id}`;
        },        
        getAssociatedDocumentKey: function (doc, associatedDocumentId, index) {

            let token = doc.historical ? 'historical-' : '';
            return 'attorney-' + token + '-' + this.documentType + '-' + doc.id + '-associated-' + associatedDocumentId + '-' + index;
        },
        associate: function (document, isConfirmation = false) {

            let url = document.historical ?
                `/ajax/attorney/claims/${document.claim_id}/historical/documents/${document.historical_document_id}/dispute/associations` :
                `/ajax/attorney/claims/${document.claim_id}/documents/${document.document_id}/dispute/associations`;
                
            let callback = this.associateDocument;
            
            if (isConfirmation) {

                url = `/ajax/attorney/claims/${document.claim_id}/documents/${document.document_id}/confirmations/${document.outbound_id}/dispute/associations?method=${document.transmission_method_id}`;
                callback = this.associateConfirmation;
                document.description = document.status_description + ' ' + this.$options.filters.localizeTime(document.completed_at, 'YYYY-MM-DD HH:mm:ss');
            }

            if (this.disputeCount > 1) {

                // allow the user to select which 1008(s) to associate with
                eventHub.$emit('show-attorney-dispute-association', document, url, callback);                
            }
            else if (this.disputeCount === 1) {

                callback(document, null, null);
            }
        },
        associateDocument: function (document, disputeIds) {

            let url = document.historical ?
                `/ajax/attorney/claims/${this.claimId}/historical/documents/associate` :
                `/ajax/attorney/claims/${this.claimId}/documents/associate`;

            let documentId = document.historical ? document.historical_document_id : document.document_id;

            axios.post(url, { 
                document_id: documentId,
                dispute_ids: disputeIds
            })
            .catch(error => {
                toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
            });
        },
        associateConfirmation: function (confirmation, disputeIds) {

            let url = `/ajax/attorney/claims/${this.claimId}/documents/${confirmation.document_id}/confirmations/associate`;
            axios.post(url, {
                outbound_id: confirmation.outbound_id,
                transmission_method_id: confirmation.transmission_method_id,
                dispute_ids: disputeIds
            })
            .catch(error => {
                toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
            });
        },
        showSubmissionHistory: function (document) {

            eventHub.$emit('show-basic-submission-history', document);
        },            
        onDocumentNotes: function (doc) {

            let id = doc.document_id ?? doc.id;
            eventHub.$emit('show-document-notes', this.clientId, this.claimId, id, doc.document_type.id, doc.historical);
        },
        getServiceUrl: function (doc) {
            return doc.historical ? 
                `/clients/${this.clientId}/claims/${this.claimId}/historical/services/${doc.id}` :
                `/claims/${this.claimId}/documents/${doc.document_id}/services`;
        },
        getDocumentUrl: function (doc) {
            return doc.historical ?
                `/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${doc.historical_document_id}` :
                `/claims/${this.claimId}/documents/${doc.document_id}`;
        },
        getLocalDate: function (value) {

            let retval = null;

            if (value) {
                let date = new Date(value);
                retval = new Date(date.getTime() - (date.getTimezoneOffset() * 60 * 1000)).toISOString().split('T')[0];
            }

            return retval;
        },
        getSubmitDate: function (document) {
            return document.submit_date ?? this.$options.filters.localizeTime(document.submitted_at, 'YYYY-MM-DD HH:mm:ss');
        }                                    
    },
    created() {

        // add the search options
        this.searchOptions.push({ id: this.DESCRIPTION_OPTION, name: 'Description', type: 'text', placeholder: 'Search Descriptions', property: 'description' });
        this.searchOptions.push({ id: this.PAN_OPTION, name: 'PAN', type: 'text', placeholder: 'Search Patient Account Number', property: 'pan' });
        this.searchOptions.push({ id: this.DOS_OPTION, name: 'DOS', type: 'date', placeholder: 'Search Date of Service', property: 'dos' });

        // set the default search option
        this.searchOption = this.searchOptions.find(x => x.id === this.DOS_OPTION);

        this.refresh(true);

        // listen for laravel echo events
        window.Echo.private('claims.' + this.claimId)
            .listen('DocumentEvent', (e) => {
                this.refresh(false); 
            });
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }          
}
</script>

<style scoped>
    .font-xs {
        font-size: 0.6em;
    }

    .table-sm td {
        padding-top: 0.25rem !important;
        padding-bottom: 0.25rem !important;
        vertical-align: middle;
    }
    
    .table-stripe  {
        background-color:rgba(0,0,0,.05);
    }

    .input-group {
        align-self: flex-start;
    }

    i { vertical-align: middle !important; }
</style>